import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { openRazorpayCheckout, RAZORPAY_KEY } from "@/lib/razorpay";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Plan } from "@shared/plans";
import { useNavigate } from "react-router-dom";

export function useCheckout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const checkout = async (item: Plan | { type: 'ADDON', amount: number }) => {
    if (!user) {
      if ('code' in item) {
        localStorage.setItem("pendingPlan", item.code);
      }
      navigate(`/auth?next=/checkout`);
      return;
    }

    setLoading(true);
    try {
      if ('code' in item) { // It's a Plan
        const { mode, orderId, subscriptionId, key, redirectUrl } =
          await apiRequest("/api/billing/checkout", "POST", { planCode: item.code }).then(r => r.json());

        await openRazorpayCheckout({
          key,
          amount: item.monthlyPricePaise, // Use monthlyPricePaise for plans
          currency: "INR",
          name: "StoryLikho",
          description: item.name, // Plan name as description
          order_id: orderId,
          subscription_id: subscriptionId,
          notes: {planCode: item.code, userId: user.id.toString()},
          handler: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });
            queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
            navigate("/dashboard");
          },
          modal: { ondismiss: () => setLoading(false) },
        });
      } else { // It's an Add-on
        const { orderId, key, ...rest } = await apiRequest("/api/billing/checkout", "POST", {
          planCode: "ADDON",
          customAmount: item.amount
        }).then(r => r.json());

        await openRazorpayCheckout({
          key,
          order_id: orderId,
          amount: item.amount, // Amount in paise
          currency: "INR",
          name: "StoryLikho",
          description: "Add-on Credits",
          handler: () => {
            toast({ title: "Purchase successful!" });
            queryClient.invalidateQueries({ queryKey: ["/api/credits/stats"] });
          },
          modal: { ondismiss: () => setLoading(false) },
        });
      }
    } catch (e:any) {
      toast({ title: "Payment failed", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return { handleCheckout: checkout, loading };
}